<script lang="ts">
  import { selectedCustomerAccount } from "/@/stores/CustomerAccount"
  import { NOTES_OPTIONS } from "/@lib/constants/notesOptions"
  import { twApiClient } from "/@lib/tastyworks-rest"
  import { createForm } from "@tastyworks/svelte-forms-lib"
  import { ValidateYup, type Account } from "@tastyworks/tastyworks-api"
  import {
    Button,
    Checkbox,
    Dialog,
    Input,
    Label,
    Select,
  } from "@tastyworks/ui-library"

  type Props = {
    onCreateNewNote: (values: {
      subject: string
      type: string
      subType: string
      body: string
      accountNumbers: string[]
    }) => void
  }

  const { onCreateNewNote }: Props = $props()

  let open = $state(false)

  // Open state for each of the three in-dialog dropdowns. Bound to the
  // design-system Select.Root for type/subType (so we can react to
  // their open state from outside) and tracked locally for the hand-
  // rolled accounts dropdown. Used by the Esc interceptor and the
  // dynamic z-index logic so that the currently-open dropdown rises
  // above the closed ones.
  let typeOpen = $state(false)
  let subTypeOpen = $state(false)
  let accountsMenuOpen = $state(false)

  // ATP-14088: accounts the user can dispatch this note to. Fetched on
  // open from /customers/{id}/accounts so the multi-select reflects the
  // currently-viewed customer's full portfolio. Seeded with the
  // right-clicked row's account synchronously so the panel has at least
  // one option to render while the fetch is in flight.
  let availableAccounts: Account[] = $state([])
  // Account numbers currently ticked. Defaults to [currentlyViewedAccount]
  // each time the dialog opens. Submit is gated on this being non-empty.
  let selectedAccountNumbers: string[] = $state([])
  // Monotonic counter so a slow in-flight fetch can't overwrite a
  // newer one's results (open → close → reopen on a different customer
  // would otherwise stomp the new state).
  let accountsFetchToken = 0

  const allSelected = $derived(
    availableAccounts.length > 0 &&
      selectedAccountNumbers.length === availableAccounts.length
  )

  // The account that was being viewed when the dialog opened — bolded
  // in the list so the user can tell which one they "came from" at a
  // glance even if other accounts share the same nickname.
  const currentAccountNumber = $derived(
    $selectedCustomerAccount.account?.accountNumber
  )

  const { form, handleSubmit, errors } = createForm({
    initialValues: {
      subject: "",
      type: "",
      subType: "",
      body: "",
    },
    onSubmit: async (values) => {
      // Belt-and-suspenders: Submit is also disabled when this is
      // empty, but the form library can resubmit via Enter key in
      // some edge cases — bail early rather than dispatch nothing.
      if (selectedAccountNumbers.length === 0) return
      onCreateNewNote({
        ...values,
        accountNumbers: [...selectedAccountNumbers],
      })
      open = false
      clearNewNote()
    },
    validationSchema: ValidateYup.noteSchema,
  })

  let subTypeOptions: string[] = $state([])

  const clearNewNote = () => {
    form.set({
      subject: "",
      type: "",
      subType: "",
      body: "",
    })
    subTypeOptions = []
    accountsMenuOpen = false
    typeOpen = false
    subTypeOpen = false
  }

  $effect(() => {
    if (!open) return
    const currentToken = ++accountsFetchToken
    const currentAccount = $selectedCustomerAccount.account
    const accountNumber = currentAccount?.accountNumber
    // Seed both lists synchronously so the dialog never opens in a
    // state where Submit is disabled by default for a no-good-reason.
    selectedAccountNumbers = accountNumber ? [accountNumber] : []
    availableAccounts = currentAccount ? [currentAccount] : []
    accountsMenuOpen = false

    const customerId = $selectedCustomerAccount.customer?.id
    if (!customerId) return

    twApiClient.AdminCustomerAccountService.fetchAccountsByCustomerId(
      customerId
    )
      .then((response) => {
        if (currentToken !== accountsFetchToken) return
        const fetched = response.items ?? []
        if (fetched.length === 0) return
        availableAccounts = fetched
        // If the currently-viewed account isn't in the fetched list —
        // unusual but possible if /customers-accounts and
        // /customers/{id}/accounts disagree about ownership — drop it
        // from the default selection so the visible count stays honest.
        const fetchedNumbers = new Set(fetched.map((a) => a.accountNumber))
        selectedAccountNumbers = selectedAccountNumbers.filter((n) =>
          fetchedNumbers.has(n)
        )
      })
      .catch((error) => {
        if (currentToken !== accountsFetchToken) return
        console.error(
          "Failed to fetch customer accounts for note dispatch",
          customerId,
          error
        )
        // Leave availableAccounts as the synchronous seed (the
        // right-clicked row's account) so the user can still post to
        // at least the current account.
      })
  })

  // Generalized Esc interceptor. While ANY in-dialog dropdown is open
  // (accounts, type, or subType), capture Escape at the window level
  // so it closes the dropdown without bubbling up to Dialog.Root's
  // built-in Escape handler (which would tear down the whole modal
  // and drop the user's in-progress note content).
  //
  // Mechanism:
  // - Capture phase propagates outer → inner (window → document →
  //   target), so a window-level capture listener fires before any
  //   document-level listener regardless of registration order. That
  //   beats bits-ui's Dialog.Root Escape handler (registered on
  //   document) and the design-system Select.Root's internal Escape
  //   handler (also registered on document via SelectRoot.svelte).
  // - preventDefault + stopPropagation make sure neither of those
  //   handlers gets the event.
  // - We then close all three open flags. Since the design-system
  //   Select.Root has open as bindable, setting typeOpen/subTypeOpen
  //   to false propagates through the bind back into Select.Root and
  //   hides its panel. Setting all three is harmless because only one
  //   is typically open at a time (clicking a new dropdown closes any
  //   previously open one via the design system's own click-outside).
  // - Listener is only registered while at least one dropdown is open,
  //   so normal Dialog Esc-to-close behaviour resumes the moment all
  //   dropdowns are closed.
  $effect(() => {
    const anyDropdownOpen = accountsMenuOpen || typeOpen || subTypeOpen
    if (!anyDropdownOpen) return
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      event.preventDefault()
      event.stopPropagation()
      accountsMenuOpen = false
      typeOpen = false
      subTypeOpen = false
    }
    window.addEventListener("keydown", handleEscape, true)
    return () => window.removeEventListener("keydown", handleEscape, true)
  })

  function toggleAccount(accountNumber: string) {
    if (!accountNumber) return
    selectedAccountNumbers = selectedAccountNumbers.includes(accountNumber)
      ? selectedAccountNumbers.filter((n) => n !== accountNumber)
      : [...selectedAccountNumbers, accountNumber]
  }

  function selectAllAccounts() {
    selectedAccountNumbers = availableAccounts
      .map((a) => a.accountNumber)
      .filter((n): n is string => Boolean(n))
  }

  function clearAllAccounts() {
    selectedAccountNumbers = []
  }

  // Inline click-outside action. Kept local to this component rather
  // than promoted to a shared util since no other tradedesk consumer
  // currently needs it. Listens on the document during the bubble
  // phase (the trigger button uses stopPropagation to avoid the
  // re-close-then-reopen race when the user clicks the trigger while
  // the panel is open).
  function clickOutside(
    node: HTMLElement,
    handler: () => void
  ): { destroy: () => void } {
    const onDocumentClick = (event: MouseEvent) => {
      if (event.target instanceof Node && !node.contains(event.target)) {
        handler()
      }
    }
    document.addEventListener("click", onDocumentClick)
    return {
      destroy: () => document.removeEventListener("click", onDocumentClick),
    }
  }

  // Shared class strings for the design-system Select.Root used by the
  // Type and Subtype dropdowns. Restyle the default panel chrome and
  // option highlighting to match the hand-rolled accounts dropdown:
  //
  // - Panel: white bg, dark border, drop shadow — same chrome as the
  //   accounts dropdown panel.
  // - Option: white bg (overrides the design system's odd/even row
  //   striping), slate hover, and an override of the default
  //   `data-[highlighted='true']:bg-blue-500 + text-white` styling from
  //   selectOptionVariants — that bright blue highlight was the most
  //   conspicuous visual difference from the accounts dropdown. The
  //   text color override prevents white-on-slate-100 (which would be
  //   unreadable).
  //
  // cn() inside SelectPanel/SelectOption uses tailwind-merge, so these
  // utility classes properly override the design-system defaults via
  // last-class-wins dedup.
  const PANEL_CLASS =
    "rounded border border-trade-desk-gray-dark bg-white shadow-lg"
  const OPTION_CLASS =
    "cursor-pointer bg-white px-3 py-2 text-sm hover:bg-slate-100 " +
    "data-[highlighted='true']:bg-slate-100 " +
    "data-[highlighted='true']:text-general-primary-text"
</script>

<Dialog.Root bind:open>
  <Dialog.Trigger
    disabled={$selectedCustomerAccount.account?.accountNumber == null}
    variant="secondary">New</Dialog.Trigger
  >

  <Dialog.Portal>
    <Dialog.Overlay />
    <Dialog.Content>
      <div class="w-full">
        <form onsubmit={handleSubmit}>
          <div class="flex flex-col gap-4">
            <Label for="subject">Subject*:</Label>
            <Input
              id="subject"
              class="border border-trade-desk-gray-dark bg-trade-desk-gray"
              bind:value={$form.subject}
              required
            />

            <Label for="type">Type*:</Label>
            {#if $errors.type}
              <p class="text-red-500">{$errors.type}</p>
            {/if}
            <!-- Dynamic z-index: when the panel is open, the whole
                 Select.Root rises to z-50 so its panel (and the panel's
                 own internal z-10 from SelectPanel.svelte) paints
                 above sibling dropdowns and the accounts trigger
                 button down in the footer. Closed dropdowns stay at
                 z-10 so they don't trap themselves under the page
                 background. -->
            <Select.Root
              id="type"
              bind:value={$form.type}
              bind:open={typeOpen}
              class={`w-full ${typeOpen ? "z-50" : "z-10"}`}
              required
              onchange={({ value }) => {
                subTypeOptions =
                  NOTES_OPTIONS.find((option) => option.header === value)
                    ?.key ?? []
              }}
            >
              {#snippet trigger()}
                <Select.Trigger
                  class="w-full border border-trade-desk-gray-dark bg-trade-desk-gray"
                  data-testid="type-select-trigger"
                />
              {/snippet}

              {#snippet panel()}
                <Select.Panel class={PANEL_CLASS}>
                  {#each NOTES_OPTIONS as option}
                    <Select.Option
                      value={option.header}
                      label={option.header}
                      class={OPTION_CLASS}
                    />
                  {/each}
                </Select.Panel>
              {/snippet}
            </Select.Root>

            <Label for="subType">Subtype:</Label>
            {#key subTypeOptions}
              <Select.Root
                id="subType"
                bind:value={$form.subType}
                bind:open={subTypeOpen}
                class={`w-full ${subTypeOpen ? "z-50" : "z-10"}`}
              >
                {#snippet trigger()}
                  <Select.Trigger
                    class="w-full border border-trade-desk-gray-dark bg-trade-desk-gray"
                    data-testid="subtype-select-trigger"
                  />
                {/snippet}

                {#snippet panel()}
                  <Select.Panel
                    class={PANEL_CLASS}
                    data-testid="subtype-select-panel"
                  >
                    {#each subTypeOptions as option}
                      <Select.Option
                        value={option}
                        label={option}
                        class={OPTION_CLASS}
                      />
                    {/each}
                  </Select.Panel>
                {/snippet}
              </Select.Root>
            {/key}

            <Label for="body">Body:</Label>
            <textarea
              id="body"
              class="h-24 w-full resize-none border border-trade-desk-gray-dark bg-trade-desk-gray p-2"
              bind:value={$form.body}></textarea>

            <div class="flex items-center justify-between">
              <!-- ATP-14088: Accounts multi-select. Rolled by hand
                   rather than reusing the design-system Select.Root
                   because that one is single-select-with-bound-value
                   and doesn't expose a way to override the trigger
                   label for a multi-pick summary count.

                   Panel chrome uses slate-* (Tailwind defaults) rather
                   than trade-desk-gray-* so the header and row-hover
                   states don't blend with the form inputs above (which
                   are bg-trade-desk-gray). Slate is a cooler gray and
                   reads as a distinct surface even though both end up
                   in the same broad value range.

                   Wrapper class is dynamic — z-50 only while the panel
                   is open — so the trigger button doesn't paint over
                   the type/subType panels when those are open. -->
              <div class={`relative ${accountsMenuOpen ? "z-50" : ""}`}>
                <Button
                  type="button"
                  variant="secondary"
                  onclick={(e: MouseEvent) => {
                    // Stop the document-level clickOutside listener
                    // from closing the panel in the same tick that
                    // this click opens it.
                    e.stopPropagation()
                    accountsMenuOpen = !accountsMenuOpen
                  }}
                  data-testid="note-accounts-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={accountsMenuOpen}
                >
                  Accounts ({selectedAccountNumbers.length}/{availableAccounts.length})
                  ▾
                </Button>
                {#if accountsMenuOpen}
                  <div
                    role="listbox"
                    aria-multiselectable="true"
                    aria-label="Accounts to apply note to"
                    use:clickOutside={() => (accountsMenuOpen = false)}
                    class="absolute bottom-full left-0 z-50 mb-1 max-h-64 w-72 overflow-y-auto rounded border border-trade-desk-gray-dark bg-white shadow-lg"
                    data-testid="note-accounts-panel"
                  >
                    <button
                      type="button"
                      class="w-full border-b border-trade-desk-gray-dark bg-slate-200 px-3 py-2 text-left text-sm font-medium hover:bg-slate-300"
                      onclick={(e: MouseEvent) => {
                        e.stopPropagation()
                        if (allSelected) {
                          clearAllAccounts()
                        } else {
                          selectAllAccounts()
                        }
                      }}
                      data-testid="note-accounts-select-all"
                    >
                      {allSelected ? "Clear all" : "Select all"}
                    </button>
                    {#each availableAccounts as account (account.accountNumber)}
                      <button
                        type="button"
                        class="flex w-full items-center gap-2 px-3 py-1 text-left text-sm hover:bg-slate-100"
                        onclick={(e: MouseEvent) => {
                          e.stopPropagation()
                          toggleAccount(account.accountNumber)
                        }}
                      >
                        <!-- Presentational: the button owns the toggle. -->
                        <Checkbox
                          checked={selectedAccountNumbers.includes(
                            account.accountNumber
                          )}
                          class="pointer-events-none"
                          tabindex={-1}
                          aria-hidden="true"
                        />
                        <span
                          class="flex-1"
                          class:font-bold={account.accountNumber ===
                            currentAccountNumber}
                        >
                          {account.accountNumber}{account.nickname
                            ? ` — ${account.nickname}`
                            : ""}
                        </span>
                      </button>
                    {/each}
                  </div>
                {/if}
              </div>

              <div class="flex gap-3">
                <Button
                  name="submit"
                  variant="success"
                  type="submit"
                  disabled={selectedAccountNumbers.length === 0}>Submit</Button
                >
                <Button
                  name="close"
                  variant="error"
                  type="button"
                  onclick={() => {
                    open = false
                    clearNewNote()
                  }}>Cancel</Button
                >
              </div>
            </div>
          </div>
        </form>
      </div></Dialog.Content
    >
  </Dialog.Portal>
</Dialog.Root>
